"use client";

import type { Product } from "@/types";
import type { StationSummary } from "@/hooks/useStationHubData";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { formatStationLocation } from "@/lib/mobility/station-location";
import { cn } from "@/lib/utils";
import { useHub } from "./hub-context";
import {
  StationNameLink,
  StationRowShell,
  StationRowSkeleton,
  StationThumb,
} from "./hub-ui";
import { formatKm } from "./StationRow";

/** 원형 겹침에 보이는 장비 사진 수 — 넘치면 "+N" 원 하나로 개수만 알린다. */
const GEAR_PREVIEW = 3;

/**
 * 캠핑장 한 줄 — [캠핑장] 목록이 쓴다(바이크는 `StationRow`).
 * 정사각 사진(`StationThumb`) / 위치 줄 / 이름 / (명소를 골랐으면) 명소까지 거리 /
 * "캠핑장 수령" 알약 / 장비 사진 원형 겹침 + "텐트 세트 N종". 오른쪽 아래 "지도" 버튼
 * (모바일·태블릿, 09-29).
 * 금액은 행에서 뺐다(10-05 미리보기 D안) — 캠핑장 시트의 장비 카드에서 보인다.
 * 사진 순서·종수는 시트 장비 그리드의 기본 순서(`visibleProducts` 그대로)와 같다.
 */
export function CampingStationRow({
  station,
  onClick,
}: {
  station: StationSummary;
  onClick: () => void;
}) {
  const { t, locale, data, visibleProducts, landmark, landmarkLabel, rawDistanceKm } =
    useHub();
  const detail = data.details.get(station.id);
  const products = visibleProducts(station);

  // 이름·주소·사진은 상세 조회 결과에만 있다 — 오기 전에는 자리만 잡는다.
  if (!detail) return <StationRowSkeleton gear />;

  const location = formatStationLocation(detail, locale);

  return (
    <StationRowShell
      stationId={station.id}
      name={detail.name}
      onClick={onClick}
      thumb={<StationThumb src={detail.imageUrl} alt={detail.name} />}
    >
      {location ? (
        <p
          className="mb-0.5 truncate text-[12px] text-gray-500 mapShow:text-[13px]"
          suppressHydrationWarning
        >
          {location}
        </p>
      ) : null}
      <StationNameLink
        stationId={station.id}
        className="line-clamp-2 min-w-0 break-words text-[15px] font-semibold text-gray-900 mapShow:text-base"
      >
        {detail.name}
      </StationNameLink>
      {landmark ? (
        <p className="mt-0.5 text-[12px] font-semibold text-orange-700 mapShow:text-[13px]">
          {t("hub.distanceFrom", {
            name: landmarkLabel,
            km: formatKm(rawDistanceKm(station.lat, station.lng)),
          })}
        </p>
      ) : null}
      {/* 이름 아래 — 모든 폭에서 같은 자리(09-29) */}
      <span className="mt-1.5 inline-block rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-medium whitespace-nowrap text-orange-700 mapShow:text-xs">
        {t("hub.camping.pickupShort")}
      </span>
      {products.length > 0 ? <GearPreview products={products} /> : null}
    </StationRowShell>
  );
}

/** 장비 사진 3장 원형 겹침 + 넘치는 수 "+N" 원 + "텐트 세트 N종". */
function GearPreview({ products }: { products: readonly Product[] }) {
  const { t } = useHub();
  const total = products.length;
  const rest = total - GEAR_PREVIEW;

  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="flex shrink-0 items-center" aria-hidden>
        {products.slice(0, GEAR_PREVIEW).map((product, index) => {
          const image = product.images?.[0];
          return (
            <span
              key={product.id}
              className={cn(
                "relative size-7 shrink-0 overflow-hidden rounded-full border-[1.5px] border-gray-300",
                image ? "bg-white" : "bg-gray-100",
                index > 0 && "-ml-2",
              )}
            >
              {image ? (
                <OptimizedImage
                  src={image}
                  alt=""
                  fill
                  sizes="28px"
                  className="object-cover"
                />
              ) : null}
            </span>
          );
        })}
        {rest > 0 ? (
          // 다음 장비 사진 위에 반투명 검정 + 흰 숫자(10-05) — 흰 바탕에 검정 투명만
          // 두면 회색 원이 되므로, 사진을 깔아 "이런 게 더 있다"로 읽히게 한다.
          <span className="relative -ml-2 inline-flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full border-[1.5px] border-gray-300 bg-gray-900 text-[10px] font-semibold tabular-nums text-white">
            {products[GEAR_PREVIEW]?.images?.[0] ? (
              <OptimizedImage
                src={products[GEAR_PREVIEW].images![0]}
                alt=""
                fill
                sizes="28px"
                className="object-cover"
              />
            ) : null}
            <span className="absolute inset-0 bg-black/55" />
            <span className="relative">{rest >= 100 ? "99+" : `+${rest}`}</span>
          </span>
        ) : null}
      </div>
      <span className="min-w-0 truncate text-[13px] text-gray-700">
        {t("hub.camping.tentSetsN", { n: total })}
      </span>
    </div>
  );
}
